/* assets/cbi-header.js
 * cbi-header: two state sticky header.
 *
 * The sticky element is the section WRAPPER, not this custom element, so
 * pinned detection reads the wrapper's rect rather than this element's. A
 * sentinel sibling cannot be used: any sibling of cbi-header lives inside the
 * sticky wrapper, so once the wrapper pins the sentinel pins with it and sits
 * at the top of the window forever, which makes pinned detection undefined.
 *
 * One rect read per animation frame from a passive scroll listener, plus a
 * read on resize. No IntersectionObserver, because the thing being measured is
 * an element that deliberately stops moving.
 *
 * The threshold is measured from the wrapper's STATIC document position, the
 * scroll offset at which it pins. That position cannot be read while the
 * wrapper is stuck: Chromium reports both offsetTop and the rect with the
 * sticky shift applied (stuck at scrollY 1280, offsetTop reads 1280, not the
 * static 1080). So it is cached from the rect on every frame the wrapper is
 * not stuck, and measured once with sticky switched off on connect and after
 * a resize, which covers a page that loads already scrolled past the pin.
 *
 * A page can hold the header at rest for longer: while an element with
 * data-cbi-header-hold is on the page (the Interiors project intro), the
 * header stays at rest until that element's bottom has passed under the
 * header's bottom edge, and only then takes its scrolled state.
 *
 * Below 1100px the bar also carries the expanded menu: the toggle flips
 * data-nav on this element, which cbi-header.css reads, and the panel is
 * hidden from the accessibility tree and from the page scroll while closed.
 * Escape and a resize back to the desktop both close it, so the panel can
 * never be left open in a layout that has no way to close it.
 */

class CbiHeader extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10);
    if (Number.isNaN(this.threshold)) this.threshold = 100;

    this.wrapper = this.closest('.cbi-section--header') || this.closest('.shopify-section');

    this.staticTop = 0;
    this.ticking = false;
    this.needsMeasure = false;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.toggle = this.querySelector('[data-cbi-nav-toggle]');
    this.panel = this.querySelector('[data-cbi-nav-panel]');
    if (this.toggle && this.panel) {
      this.toggle.addEventListener('click', this.onNavClick);
      document.addEventListener('keydown', this.onKeydown);
    }
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    this.onCartRefresh = this.onCartRefresh.bind(this);
    document.addEventListener('cbi:cart:updated', this.onCartRefresh);
    window.addEventListener('pageshow', this.onCartRefresh);

    this.measure();
    this.apply();
  }

  disconnectedCallback() {
    delete document.documentElement.dataset.cbiHeaderState;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('cbi:cart:updated', this.onCartRefresh);
    window.removeEventListener('pageshow', this.onCartRefresh);
    document.removeEventListener('keydown', this.onKeydown);
    this.setNav(false);
  }

  /* ---------------------------------------------------------- Phone menu -- */

  get navOpen() {
    return this.dataset.nav === 'open';
  }

  setNav(open) {
    if (!this.toggle || !this.panel) return;
    if (open) {
      this.dataset.nav = 'open';
    } else {
      delete this.dataset.nav;
    }
    this.toggle.setAttribute('aria-expanded', String(open));
    /* Lenis drives the smooth scrolling everywhere else, so it is stopped
     * rather than fought. The panel itself stops the page moving behind it,
     * see cbi-header.css. */
    if (window.cbiLenis) {
      if (open) window.cbiLenis.stop();
      else window.cbiLenis.start();
    }
  }

  onNavClick() {
    this.setNav(!this.navOpen);
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.navOpen) {
      this.setNav(false);
      this.toggle.focus();
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.ticking = false;
      if (this.needsMeasure) {
        this.needsMeasure = false;
        this.measure();
      }
      this.apply();
    });
  }

  onResize() {
    /* Layout above the header, such as the 100svh hero, changes with the
     * viewport, so the static position is re-measured inside the next frame. */
    this.needsMeasure = true;
    this.onScroll();
    /* The panel and its button both vanish above the breakpoint, so an open
     * menu there would have nothing to close it. */
    /* 1100, not the theme's 750: the bar hands over to the phone layout
     * earlier than the page does, see cbi-header.css. */
    if (this.navOpen && window.matchMedia('(min-width: 1100px)').matches) {
      this.setNav(false);
    }
  }

  measure() {
    const target = this.wrapper || this;
    /* position: relative with no offsets lays the wrapper out exactly where
     * sticky would leave it unstuck. One synchronous layout, on connect and
     * resize only, never while scrolling. */
    const previous = target.style.position;
    target.style.position = 'relative';
    this.staticTop = target.getBoundingClientRect().top + window.scrollY;
    target.style.position = previous;
  }

  apply() {
    const target = this.wrapper || this;
    const hold = document.querySelector('[data-cbi-header-hold]');
    if (hold) {
      const held = hold.getBoundingClientRect().bottom > target.getBoundingClientRect().bottom;
      this.setState(held ? 'default' : 'scrolled');
      return;
    }

    const top = target.getBoundingClientRect().top;
    /* 0.5 rather than 0 absorbs subpixel rounding at fractional zoom levels. */
    const isPinned = top <= 0.5;

    if (!isPinned) {
      /* Not stuck, so the rect is the static position. Caching it here means a
       * fast scroll that jumps past the pin point inside one animation frame
       * still measures the threshold from where the header actually pinned. */
      this.staticTop = top + window.scrollY;
    }

    const travelled = isPinned ? window.scrollY - this.staticTop : 0;
    this.setState(isPinned && travelled >= this.threshold ? 'scrolled' : 'default');
  }

  setState(next) {
    if (this.dataset.state === next) return;
    this.dataset.state = next;
    /* Published on <html> too, so other bars (the shop sub-nav) can take
     * the same state in CSS without their own scroll listener. */
    document.documentElement.dataset.cbiHeaderState = next;
  }

  onCartRefresh() {
    /* Every count, not the first one. The bar carries two: the phone bubble
     * and the desktop Cart (n), and querySelector only ever found the bubble,
     * so the desktop one sat at whatever the page was rendered with until the
     * next load (Kurt). */
    const targets = this.querySelectorAll('[data-cart-count]');
    if (!targets.length) return;
    fetch(window.Shopify && window.Shopify.routes ? `${window.Shopify.routes.root}cart.js` : '/cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart && typeof cart.item_count === 'number') {
          for (const target of targets) target.textContent = String(cart.item_count);
        }
      })
      .catch(() => {
        /* A failed count refresh leaves the server rendered value in place. */
      });
  }
}

if (!customElements.get('cbi-header')) {
  customElements.define('cbi-header', CbiHeader);
}
